/**
 * An organisation's brand colour (Step 17c): set by Tekansa's Super Admin, used across that
 * organisation's app (buttons, links, the selected menu item, focus rings), its share links and
 * report emails. Browser-safe.
 */
export const DEFAULT_BRAND_COLOR = '#1362fb';

const HEX = /^#[0-9a-f]{6}$/i;

/** Contrast limits against white: readable buttons and links, still visible in dark mode. */
export const BRAND_CONTRAST = { min: 3, max: 15 } as const;

/** WCAG relative luminance of a #rrggbb colour. */
function luminance(hex: string): number {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio of two #rrggbb colours (1 to 21). */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/** Why a colour cannot be the brand colour (buttons and links must stay readable), or null. */
export function brandColorProblem(color: string): string | null {
  if (!HEX.test(color)) return 'Use a colour like #1362fb.';
  const onWhite = contrastRatio(color, '#ffffff');
  if (onWhite < BRAND_CONTRAST.min)
    return 'Too light: white button text and links on white would be hard to read. Choose a darker colour.';
  if (onWhite > BRAND_CONTRAST.max)
    return 'Too dark: it would disappear in dark mode. Choose a lighter colour.';
  return null;
}

export const BRAND_SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;
export type BrandShade = (typeof BRAND_SHADES)[number];

/** How much of the colour each shade keeps, mixed with white (lighter) or black (darker). */
const MIX: Record<BrandShade, [number, 'white' | 'black']> = {
  50: [7, 'white'],
  100: [14, 'white'],
  200: [27, 'white'],
  300: [44, 'white'],
  400: [64, 'white'],
  500: [84, 'white'],
  600: [100, 'white'],
  700: [84, 'black'],
  800: [68, 'black'],
  900: [54, 'black'],
  950: [38, 'black'],
};

/** The app's whole brand scale from one colour (600 = the colour itself), as CSS values. */
export function brandShades(color: string): Record<BrandShade, string> {
  const c = color.toLowerCase();
  return Object.fromEntries(
    BRAND_SHADES.map((s) => {
      const [keep, toward] = MIX[s];
      return [s, keep === 100 ? c : `color-mix(in oklab, ${c} ${keep}%, ${toward})`];
    }),
  ) as Record<BrandShade, string>;
}
